import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../../api';
import { useNotifications } from '../../context/NotificationContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const typeIcon = {
  requirement_created: '📋',
  transfer_pending:    '⏳',
  transfer_sent:       '🚚',
  transfer_approved:   '✅',
  transfer_rejected:   '❌',
  transfer_completed:  '📦',
  suggestion_ready:    '💡',
  procurement_sent:    '🚚',
  procurement_received:'📥',
  worker_request_created:   '📥',
  worker_request_fulfilled: '✅',
  worker_request_replied:   '💬',
  worker_request_closed:    '🚫',
  worker_item_issued:       '📦',
  worker_item_returned:     '📤',
  damage_report_pending:    '⚠️',
  damage_report_approved:   '✅',
  damage_report_rejected:   '❌',
  damage_report_withdrawn:  '↩️',
};

const typeLabel = {
  requirement_created: 'Requirement',
  transfer_pending:    'Transfer',
  transfer_sent:       'Transfer',
  transfer_approved:   'Transfer',
  transfer_rejected:   'Transfer',
  transfer_completed:  'Transfer',
  suggestion_ready:    'Suggestion',
  procurement_sent:    'Procurement',
  procurement_received:'Procurement',
  worker_request_created:   'Request',
  worker_request_fulfilled: 'Request',
  worker_request_replied:   'Request',
  worker_request_closed:    'Request',
  worker_item_issued:       'Item Issued',
  worker_item_returned:     'Item Returned',
  damage_report_pending:    'Damage',
  damage_report_approved:   'Damage',
  damage_report_rejected:   'Damage',
  damage_report_withdrawn:  'Damage',
};

// Worker-facing types (notifyWorker — sent to the requesting/holding
// employee themselves) always go to their own My Profile, which is where
// "My Requests" and their held materials/stationery/IT actually live — NOT
// the admin Workers management page. Admin/management-facing types
// (notifyUsersWithPermission — sent to whoever holds request_management or
// damage_approval) go to the Inventory page's Requests/Damage Approval tabs.
const WORKER_FACING_TYPES = new Set([
  'worker_request_fulfilled', 'worker_request_replied', 'worker_request_closed',
  'worker_item_issued', 'worker_item_returned',
]);
const MANAGEMENT_FACING_TYPES = new Set([
  'worker_request_created',
  'damage_report_pending', 'damage_report_approved', 'damage_report_rejected', 'damage_report_withdrawn',
]);

const getNavUrl = ({ type, entity, entityId }) => {
  if (entity === 'requirement' && entityId) return `/requirements/${entityId}`;
  if (entity === 'transfer'    && entityId) return `/transfers/${entityId}`;
  if (entity === 'inventory'   && entityId) return `/inventory`;
  if (WORKER_FACING_TYPES.has(type)) return '/profile';
  if (MANAGEMENT_FACING_TYPES.has(type)) return '/inventory';
  switch (type) {
    case 'requirement_created':
    case 'suggestion_ready':   return '/requirements';
    case 'transfer_pending':
    case 'transfer_sent':
    case 'transfer_approved':
    case 'transfer_rejected':
    case 'transfer_completed': return '/transfers';
    default:                   return null;
  }
};

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export default function NotificationList() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const { adjustCount }                   = useNotifications();
  const navigate                          = useNavigate();

  const load = async () => {
    try {
      const res = await notificationApi.list();
      const rows = res.data.data || [];
      setNotifications(rows);

      // Auto-mark all unread as read the moment the panel opens.
      const unread = rows.filter((n) => !n.isRead);
      if (unread.length > 0) {
        await notificationApi.markAllRead().catch(() => {});
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        adjustCount(-unread.length);
      }
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Click → mark as read (if not already) + navigate
  const handleClick = async (n) => {
    if (!n.isRead) {
      try { await notificationApi.markRead(n.id); } catch { /* ignore */ }
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
      );
      adjustCount(-1);
    }
    const url = getNavUrl(n);
    if (url) navigate(url);
  };

  const markAll = async () => {
    const unreadCount = notifications.filter((n) => !n.isRead).length;
    if (!unreadCount) return;
    await notificationApi.markAllRead();
    setNotifications((prev) => prev.map((x) => ({ ...x, isRead: true })));
    adjustCount(-unreadCount);
  };

  const handleDelete = async (e, n) => {
    e.stopPropagation();
    try { await notificationApi.remove(n.id); } catch { return; }
    setNotifications((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.isRead) adjustCount(-1);
  };

  const deleteAll = async () => {
    const unreadCount = notifications.filter((n) => !n.isRead).length;
    await Promise.all(notifications.map((n) => notificationApi.remove(n.id).catch(() => {})));
    setNotifications([]);
    if (unreadCount > 0) adjustCount(-unreadCount);
  };

  if (loading) return <LoadingSpinner />;

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            Notifications
            {unreadCount > 0 && (
              <span className="text-sm font-semibold bg-blue-600 text-white px-2 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </span>
        }
        action={
          notifications.length > 0 && (
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button className="btn-secondary text-sm" onClick={markAll}>
                  Mark all as read
                </button>
              )}
              <button
                className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50"
                onClick={deleteAll}
              >
                Clear all
              </button>
            </div>
          )
        }
      />

      {notifications.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">
          <p className="text-4xl mb-3">🔔</p>
          <p className="font-medium text-gray-500">All caught up</p>
          <p className="text-sm mt-1">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => {
            const url      = getNavUrl(n);
            const isUnread = !n.isRead;
            return (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={`
                  relative flex gap-4 items-start rounded-xl border px-4 py-3.5 transition-all duration-200
                  ${isUnread
                    ? 'bg-white border-blue-300 shadow-md cursor-pointer hover:shadow-lg hover:border-blue-400'
                    : 'bg-gray-50 border-gray-200 cursor-default hover:bg-gray-100'
                  }
                  ${url && isUnread ? 'cursor-pointer' : ''}
                `}
              >
                {/* Unread accent strip */}
                {isUnread && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-xl" />
                )}

                {/* Icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xl
                  ${isUnread ? 'bg-blue-50' : 'bg-gray-100'}`}
                >
                  {typeIcon[n.type] || '🔔'}
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {isUnread && (
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-blue-600 text-white px-1.5 py-0.5 rounded-full leading-none">
                        New
                      </span>
                    )}
                    {typeLabel[n.type] && (
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full leading-none
                        ${isUnread ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}
                      >
                        {typeLabel[n.type]}
                      </span>
                    )}
                    <span className={`text-xs ${isUnread ? 'text-gray-400' : 'text-gray-400'} ml-auto`}>
                      {timeAgo(n.createdAt)}
                    </span>
                  </div>

                  <p className={`text-sm leading-snug ${isUnread ? 'font-semibold text-gray-900' : 'font-normal text-gray-500'}`}>
                    {n.title}
                  </p>
                  <p className={`text-sm mt-0.5 ${isUnread ? 'text-gray-600' : 'text-gray-400'}`}>
                    {n.message}
                  </p>

                  {url && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleClick(n); }}
                      className={`mt-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-md border transition-colors
                        ${isUnread
                          ? 'text-blue-600 border-blue-200 hover:bg-blue-50'
                          : 'text-gray-500 border-gray-200 hover:bg-gray-100'
                        }`}
                    >
                      View →
                    </button>
                  )}
                </div>

                {/* Right: dot + delete */}
                <div className="flex flex-col items-center gap-2 flex-shrink-0 pt-0.5">
                  {isUnread
                    ? <span className="w-3 h-3 bg-blue-500 rounded-full shadow-sm shadow-blue-300 ring-2 ring-blue-100" />
                    : <span className="w-3 h-3 rounded-full border-2 border-gray-300" />
                  }
                  <button
                    onClick={(e) => handleDelete(e, n)}
                    className="text-gray-300 hover:text-red-500 transition-colors p-0.5 rounded leading-none"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
